import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

// PDF export configuration
const PDF_CONFIG = {
  format: 'a4',
  orientation: 'portrait',
  unit: 'mm',
  margin: {
    top: 15,
    right: 15,
    bottom: 15,
    left: 15
  }
};

// A4 dimensions in mm
const A4_WIDTH = 210;
const A4_HEIGHT = 297;
const CONTENT_WIDTH = A4_WIDTH - PDF_CONFIG.margin.left - PDF_CONFIG.margin.right;

/**
 * Export results to PDF - questions as text, results as image
 * @param {Object} data - Results data including scores and answers
 * @param {HTMLElement} resultsElement - The results DOM element to capture as image
 * @param {string} filename - The filename for the PDF
 * @returns {Promise<void>}
 */
export const exportToPDF = async (data, resultsElement, filename = 'kompetencia_felmeres_eredmeny.pdf') => {
  try {
    // Show loading state
    const loadingDiv = document.createElement('div');
    loadingDiv.innerHTML = `
      <div style="
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background: rgba(0, 0, 0, 0.7);
        display: flex;
        justify-content: center;
        align-items: center;
        z-index: 10000;
        color: white;
        font-size: 18px;
        font-family: Inter, sans-serif;
      ">
        <div style="text-align: center;">
          <div style="margin-bottom: 20px;">PDF generálása...</div>
          <div style="width: 40px; height: 40px; border: 4px solid #4BAC9B; border-top: 4px solid transparent; border-radius: 50%; animation: spin 1s linear infinite; margin: 0 auto;"></div>
        </div>
      </div>
      <style>
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      </style>
    `;
    // Mark overlay for removal in cloned docs
    loadingDiv.id = 'pdf-loading-overlay';
    document.body.appendChild(loadingDiv);

    // Initialize PDF
    const pdf = new jsPDF({
      orientation: PDF_CONFIG.orientation,
      unit: PDF_CONFIG.unit,
      format: PDF_CONFIG.format
    });

    let currentY = PDF_CONFIG.margin.top;

    // Helper: ensure fonts are ready if supported
    const waitForFontsReady = async () => {
      try {
        if (document.fonts && document.fonts.ready) {
          await document.fonts.ready;
        }
      } catch (_) {}
    };

    // Render the textual section as multiple small canvases (header + each question block)
    const renderQuestionsAsCanvases = async (data) => {
      await waitForFontsReady();

      // Build container
      const container = document.createElement('div');
      const targetWidthPx = Math.round(CONTENT_WIDTH * 2.83); // approximate px width to match PDF mm width
      container.style.cssText = `
        position: fixed;
        left: -10000px;
        top: 0;
        width: ${targetWidthPx}px;
        background: #ffffff;
        color: #000000;
        font-family: Inter, Arial, Helvetica, sans-serif;
        line-height: 1.4;
        padding: 0;
        margin: 0;
      `;

      // Header block
      const header = document.createElement('div');
      header.style.cssText = `
        margin-bottom: 10px;
      `;
      header.innerHTML = `
        <div style="font-size: 16px; font-weight: 700; color: rgb(75, 172, 155);">Kompetencia felmérés - Eredmények</div>
        <div style="font-size: 10px; color: #808080;">Készült: ${new Date().toLocaleDateString('hu-HU')}</div>
      `;
      container.appendChild(header);

      let list = null;
      if (data.questions && data.answers && data.questions.length > 0) {
        const title = document.createElement('div');
        title.style.cssText = `
          font-size: 12px;
          font-weight: 700;
          color: rgb(75, 172, 155);
          margin: 10px 0 6px 0;
        `;
        title.textContent = 'Válaszok:';
        container.appendChild(title);

        list = document.createElement('div');
        list.style.cssText = `
          display: block;
          font-size: 9px;
          color: #111111;
        `;

        const completeQuestions = [];
        for (let i = 1; i <= 32; i++) {
          const question = data.questions.find(q => q.id === i);
          if (question) completeQuestions.push(question);
        }

        completeQuestions.forEach((question) => {
          const answer = data.answers[question.id];
          if (!answer) return;

          const item = document.createElement('div');
          item.style.cssText = `margin-bottom: 6px;`;

          const qText = document.createElement('div');
          qText.style.cssText = `font-weight: 400; color: #000;`;
          qText.textContent = `${question.id}. ${question.text}`;
          item.appendChild(qText);

          const aText = document.createElement('div');
          aText.style.cssText = `font-weight: 700; color: rgb(75, 172, 155); margin-left: 5px;`;
          aText.textContent = `Válasz: ${answer}/5`;
          item.appendChild(aText);

          list.appendChild(item);
        });

        container.appendChild(list);
      }

      document.body.appendChild(container);
      // Small delay to allow layout and webfonts
      await new Promise(resolve => setTimeout(resolve, 100));

      // Capture header
      const canvases = [];
      const captureOptions = {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#ffffff',
        logging: false
      };
      const headerCanvas = await html2canvas(header, captureOptions);
      canvases.push(headerCanvas);

      // Capture title "Válaszok:"
      const titleEl = container.childNodes[1];
      if (titleEl) {
        const titleCanvas = await html2canvas(titleEl, captureOptions);
        canvases.push(titleCanvas);
      }

      // Capture each question block individually
      const questionBlocks = list ? Array.from(list.children) : [];
      for (const block of questionBlocks) {
        const blockCanvas = await html2canvas(block, captureOptions);
        canvases.push(blockCanvas);
      }

      document.body.removeChild(container);
      return canvases;
    };

    // 1) Add the text section canvases sequentially with safe page breaks
    const textCanvases = await renderQuestionsAsCanvases(data);
    if (textCanvases && textCanvases.length) {
      for (const canvas of textCanvases) {
        const imgWidth = canvas.width;
        const imgHeight = canvas.height;
        const scale = (CONTENT_WIDTH * 2.83) / imgWidth;
        const imageHeight = (imgHeight * scale) / 2.83;

        const availableHeight = A4_HEIGHT - currentY - PDF_CONFIG.margin.bottom;
        if (imageHeight > availableHeight) {
          pdf.addPage();
          currentY = PDF_CONFIG.margin.top;
        }

        const imgData = canvas.toDataURL('image/jpeg', 0.95);
        pdf.addImage(imgData, 'JPEG', PDF_CONFIG.margin.left, currentY, CONTENT_WIDTH, imageHeight);
        currentY += imageHeight + 2;
      }
      // Start results on a new page
      pdf.addPage();
      currentY = PDF_CONFIG.margin.top;
    }

    // Capture results section in blocks to avoid cutting text
    if (resultsElement) {
      // Helper: add a canvas to the PDF with proper pagination, updating currentY
      const addCanvasToPdf = async (canvas) => {
        const imgWidth = canvas.width;
        const imgHeight = canvas.height;
        const scale = (CONTENT_WIDTH * 2.83) / imgWidth;
        const scaledHeight = (imgHeight * scale) / 2.83;

        let availableHeight = A4_HEIGHT - currentY - PDF_CONFIG.margin.bottom;
        if (availableHeight < 10) {
          pdf.addPage();
          currentY = PDF_CONFIG.margin.top;
        }

        let maxHeightPerPage = A4_HEIGHT - currentY - PDF_CONFIG.margin.bottom;
        let totalPages = Math.ceil(scaledHeight / maxHeightPerPage);

        for (let page = 0; page < totalPages; page++) {
          if (page > 0) {
            pdf.addPage();
            currentY = PDF_CONFIG.margin.top;
            maxHeightPerPage = A4_HEIGHT - currentY - PDF_CONFIG.margin.bottom;
          }

          const sourceY = (page * maxHeightPerPage * 2.83) / scale;
          const sourceHeight = Math.min((maxHeightPerPage * 2.83) / scale, imgHeight - sourceY);
          if (sourceHeight <= 0) break;

          const pageCanvas = document.createElement('canvas');
          const pageCtx = pageCanvas.getContext('2d');
          pageCanvas.width = imgWidth;
          pageCanvas.height = sourceHeight;
          pageCtx.drawImage(canvas, 0, sourceY, imgWidth, sourceHeight, 0, 0, imgWidth, sourceHeight);

          const imgData = pageCanvas.toDataURL('image/jpeg', 0.9);
          const imageHeight = Math.min(maxHeightPerPage, (sourceHeight * scale) / 2.83);
          pdf.addImage(imgData, 'JPEG', PDF_CONFIG.margin.left, currentY, CONTENT_WIDTH, imageHeight);

          // Move currentY below the image on this page to stack next content
          currentY += imageHeight + 2;
        }
      };

      // Helper: capture a single element as canvas, removing filters in the clone
      const captureElementAsCanvas = async (el) => {
        el.setAttribute('data-pdf-block', '1');
        try {
          const canvas = await html2canvas(el, {
            scale: 2,
            useCORS: true,
            allowTaint: true,
            backgroundColor: '#ffffff',
            logging: false,
            width: el.scrollWidth,
            height: el.scrollHeight,
            onclone: function(clonedDoc) {
              const target = clonedDoc.querySelector('[data-pdf-block="1"]');
              // Hide non-essential UI
              clonedDoc.querySelectorAll('.action-buttons').forEach(btn => { btn.style.display = 'none'; });
              // Remove loading overlay if present
              const overlay = clonedDoc.getElementById('pdf-loading-overlay');
              if (overlay) overlay.remove();
              // Remove visual filters
              if (target) {
                target.style.filter = 'none';
                target.style.opacity = '1';
                target.querySelectorAll('*').forEach(child => {
                  child.style.filter = 'none';
                  child.style.opacity = '1';
                });
              }
            }
          });
          return canvas;
        } finally {
          el.removeAttribute('data-pdf-block');
        }
      };

      // Add results title
      pdf.setFontSize(12);
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(75, 172, 155);
      pdf.text('Eredmények:', PDF_CONFIG.margin.left, currentY);
      currentY += 8;

      // Determine fine-grained result blocks to avoid cutting text
      const blocks = [];
      // Section summaries: capture each summary card separately
      const summaryCards = resultsElement.querySelectorAll('.section-summaries .section-summary');
      summaryCards.forEach(card => blocks.push(card));
      // For each section: header then each competency line
      const sectionContainers = resultsElement.querySelectorAll('.section-results');
      sectionContainers.forEach(section => {
        const header = section.querySelector('h4');
        if (header) blocks.push(header);
        section.querySelectorAll('.competency-result').forEach(item => blocks.push(item));
      });
      // Recommendations: capture header, intro text, and each recommendation item
      const rec = resultsElement.querySelector('.development-recommendations');
      if (rec) {
        const recHeader = rec.querySelector('h3');
        if (recHeader) blocks.push(recHeader);
        rec.querySelectorAll('p').forEach(p => blocks.push(p));
        rec.querySelectorAll('.recommendation-item').forEach(item => blocks.push(item));
      }

      for (const block of blocks) {
        const blockCanvas = await captureElementAsCanvas(block);
        await addCanvasToPdf(blockCanvas);
        // Small spacing between blocks
        currentY += 2;
      }
    }

    // Add page numbers
    const totalPdfPages = pdf.internal.getNumberOfPages();
    for (let i = 1; i <= totalPdfPages; i++) {
      pdf.setPage(i);
      pdf.setFontSize(8);
      pdf.setTextColor(128, 128, 128);
      pdf.text(`${i} / ${totalPdfPages}`, A4_WIDTH - PDF_CONFIG.margin.right - 15, A4_HEIGHT - PDF_CONFIG.margin.bottom + 5);
    }

    // Remove loading indicator
    if (loadingDiv && document.body.contains(loadingDiv)) {
      document.body.removeChild(loadingDiv);
    }

    // Save the PDF
    pdf.save(filename);

    // Show success message
    showNotification('PDF sikeresen generálva!', 'success');

  } catch (error) {
    console.error('PDF export error:', error);

    // Remove loading indicator if it exists
    const existingLoadingDiv = document.getElementById('pdf-loading-overlay');
    if (existingLoadingDiv && document.body.contains(existingLoadingDiv)) {
      document.body.removeChild(existingLoadingDiv);
    }

    showNotification('Hiba történt a PDF generálása során.', 'error');
  }
};

/**
 * Show notification to user
 */
const showNotification = (message, type = 'info') => {
  const notification = document.createElement('div');
  notification.innerHTML = `
    <div style="
      position: fixed;
      top: 20px;
      right: 20px;
      background: ${type === 'success' ? '#4BAC9B' : '#ef4444'};
      color: white;
      padding: 15px 20px;
      border-radius: 8px;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
      z-index: 10000;
      font-family: Inter, sans-serif;
      font-size: 14px;
      font-weight: 500;
      max-width: 300px;
      animation: slideIn 0.3s ease-out;
    ">
      ${message}
    </div>
    <style>
      @keyframes slideIn {
        from {
          transform: translateX(100%);
          opacity: 0;
        }
        to {
          transform: translateX(0);
          opacity: 1;
        }
      }
    </style>
  `;

  document.body.appendChild(notification);

  // Remove notification after 3 seconds
  setTimeout(() => {
    notification.style.animation = 'slideIn 0.3s ease-out reverse';
    setTimeout(() => {
      if (document.body.contains(notification)) {
        document.body.removeChild(notification);
      }
    }, 300);
  }, 3000);
};

export default exportToPDF;